import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingBag, Search, User, Menu, X, Instagram, Facebook, Youtube, Twitter, Music2, MessageCircle, Globe, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

const links = [
    { label: "Inicio", to: "/" },
    { label: "Tienda", to: "/tienda" },
    { label: "Hombre", to: "/tienda?genero=Hombre" },
    { label: "Mujer", to: "/tienda?genero=Mujer" },
    { label: "Accesorios", to: "/tienda?categoria=Accesorios" },
    { label: "Ofertas", to: "/tienda?oferta=1" },
];

export function Header() {
    const [open, setOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [q, setQ] = useState("");
    const { user, logout } = useAuth();
    const { totalItems } = useCart();
    const nav = useNavigate();
    const { data: settings } = useQuery({ queryKey: ["settings-header"], queryFn: async () => (await api.get("/settings")).data });
    const store = settings?.store_name || "J&M AMERICAN SHOP";

    const submit = (e) => { e.preventDefault(); setSearchOpen(false); nav(`/tienda?q=${encodeURIComponent(q)}`); };

    return (
        <header className="sticky top-0 z-40 bg-bone2/90 backdrop-blur-md border-b border-sandLight">
            <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
                <div className="flex h-16 sm:h-20 items-center justify-between gap-4">
                    <Link to="/" data-testid="nav-link-home" className="font-display text-lg sm:text-2xl tracking-wide text-ink shrink-0">{store}</Link>
                    <nav className="hidden lg:flex items-center gap-7">
                        {links.map((l) => (
                            <NavLink key={l.label} to={l.to} className="text-[13px] uppercase tracking-[0.15em] text-ink/75 hover:text-ink transition-colors">{l.label}</NavLink>
                        ))}
                    </nav>
                    <div className="flex items-center gap-1 sm:gap-3">
                        <button onClick={() => setSearchOpen((v) => !v)} className="p-2 text-ink hover:opacity-70" aria-label="Buscar"><Search className="w-5 h-5" /></button>
                        {user ? (
                            <div className="hidden sm:flex items-center gap-2">
                                {user.role === "admin" && (
                                    <Link to="/admin" data-testid="nav-link-admin" className="p-2 text-ink hover:opacity-70" title="Admin"><LayoutDashboard className="w-5 h-5" /></Link>
                                )}
                                <Link to="/cuenta" data-testid="nav-link-user" className="p-2 text-ink hover:opacity-70" title="Mi cuenta"><User className="w-5 h-5" /></Link>
                                <button onClick={logout} className="p-2 text-ink hover:opacity-70" title="Salir"><LogOut className="w-4 h-4" /></button>
                            </div>
                        ) : (
                            <Link to="/login" data-testid="nav-link-login" className="hidden sm:inline-flex items-center gap-1 text-[13px] uppercase tracking-[0.14em] text-ink hover:opacity-70 px-2 py-2">
                                <User className="w-4 h-4" /> Ingresar
                            </Link>
                        )}
                        <Link to="/carrito" data-testid="nav-link-cart" className="relative p-2 text-ink hover:opacity-70">
                            <ShoppingBag className="w-5 h-5" />
                            {totalItems > 0 && <span className="absolute -top-0.5 -right-0.5 bg-ink text-bone text-[10px] leading-none rounded-full w-4 h-4 flex items-center justify-center">{totalItems}</span>}
                        </Link>
                        <button onClick={() => setOpen((v) => !v)} className="lg:hidden p-2 text-ink" aria-label="Menú">{open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
                    </div>
                </div>
                {searchOpen && (
                    <form onSubmit={submit} className="pb-4">
                        <input autoFocus data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="¿Qué estás buscando?" className="w-full border border-sandLight bg-white rounded-full px-5 py-3 text-sm outline-none focus:border-ink" />
                    </form>
                )}
            </div>
            {open && (
                <div className="lg:hidden border-t border-sandLight bg-bone">
                    <nav className="flex flex-col px-4 py-3">
                        {links.map((l) => (
                            <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="py-3 text-sm uppercase tracking-wide text-ink border-b border-sandLight/70 last:border-none">{l.label}</Link>
                        ))}
                        {user ? (
                            <>
                                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="py-3 text-sm uppercase text-ink">Panel Admin</Link>}
                                <Link to="/cuenta" onClick={() => setOpen(false)} className="py-3 text-sm uppercase text-ink">Mi Cuenta</Link>
                                <button onClick={() => { logout(); setOpen(false); }} className="py-3 text-sm uppercase text-left text-terracotta">Cerrar Sesión</button>
                            </>
                        ) : (
                            <Link to="/login" onClick={() => setOpen(false)} className="py-3 text-sm uppercase text-ink">Ingresar / Registrarse</Link>
                        )}
                    </nav>
                </div>
            )}
        </header>
    );
}

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, youtube: Youtube, twitter: Twitter, x: Twitter, tiktok: Music2, whatsapp: MessageCircle };

function socialIcon(name) {
    const n = (name || "").toLowerCase();
    for (const k in SOCIAL_ICONS) if (n.includes(k)) return SOCIAL_ICONS[k];
    return Globe;
}

export function Footer() {
    const { data: settings } = useQuery({ queryKey: ["settings-footer"], queryFn: async () => (await api.get("/settings")).data });
    const socials = settings?.social_links?.length ? settings.social_links : [
        settings?.instagram && { name: "Instagram", url: settings.instagram },
        settings?.facebook && { name: "Facebook", url: settings.facebook },
        settings?.tiktok && { name: "TikTok", url: settings.tiktok },
    ].filter(Boolean);
    return (
        <footer className="mt-24 bg-ink text-bone">
            <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-14 grid gap-10 md:grid-cols-4">
                <div>
                    <p className="font-display text-2xl">{settings?.store_name || "J&M AMERICAN SHOP"}</p>
                    <p className="text-sm text-bone/60 mt-3 max-w-xs">{settings?.store_description}</p>
                </div>
                <div>
                    <p className="text-eyebrow text-bone/70 mb-4">Tienda</p>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/tienda" className="hover:text-terracotta">Catálogo</Link></li>
                        <li><Link to="/tienda?oferta=1" className="hover:text-terracotta">Ofertas</Link></li>
                        <li><Link to="/tienda?genero=Hombre" className="hover:text-terracotta">Hombre</Link></li>
                        <li><Link to="/tienda?genero=Mujer" className="hover:text-terracotta">Mujer</Link></li>
                    </ul>
                </div>
                <div>
                    <p className="text-eyebrow text-bone/70 mb-4">Cuenta</p>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/login" className="hover:text-terracotta">Iniciar Sesión</Link></li>
                        <li><Link to="/registro" className="hover:text-terracotta">Crear Cuenta</Link></li>
                        <li><Link to="/cuenta" className="hover:text-terracotta">Mis Pedidos</Link></li>
                    </ul>
                </div>
                <div>
                    <p className="text-eyebrow text-bone/70 mb-4">Síguenos</p>
                    <div className="flex gap-3 flex-wrap" data-testid="footer-socials">
                        {socials.map((s, i) => {
                            const Icon = socialIcon(s.name);
                            return <a key={i} href={s.url} target="_blank" rel="noreferrer" title={s.name} className="w-9 h-9 border border-bone/30 rounded-full flex items-center justify-center hover:bg-bone hover:text-ink transition"><Icon className="w-4 h-4" /></a>;
                        })}
                    </div>
                </div>
            </div>
            <div className="border-t border-bone/10 py-5 text-center text-xs text-bone/50">© {new Date().getFullYear()} {settings?.store_name || "J&M AMERICAN SHOP"} — Todos los derechos reservados.</div>
        </footer>
    );
}

export function PublicLayout({ children }) {
    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-1">{children}</main>
            <Footer />
        </div>
    );
}
